import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { urlsBuilderContextToken } from "@/context_tokens";
import {
    growaveConfigurationToken,
    localizationInfoProviderToken,
    modalManagerToken,
} from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import SimpleState from "@/utils/SimpleState";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsFilterQuery from "@apps/reviews/queries/ReviewsFilterQuery";
import ReviewsQueryBase from "@apps/reviews/queries/ReviewsQueryBase";
import ReviewsTranslationsQuery from "@apps/reviews/queries/ReviewsTranslationsQuery";
import ReviewsFilterService from "@apps/reviews/services/ReviewsFilterService";
import ReviewsServiceBase from "@apps/reviews/services/ReviewsServiceBase";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import ReviewsTranslationsService from "@apps/reviews/services/ReviewsTranslationsService";
import ReviewsStoreBase from "@apps/reviews/stores/ReviewsStoreBase";
import VotesStore from "@apps/reviews/stores/VotesStore";
import {
    reviewsApplicationLoggerToken,
    reviewsAuthApiClientToken,
    reviewsIconCollectionManagerToken,
} from "@apps/reviews/tokens";
import { SortingManager } from "@apps/reviews/utils/SortingManager";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container, injectable } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import ReviewsFilterStore from "../../stores/ReviewsFilterStore";
import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
import ReviewsListWidgetComponent from "./ReviewsListWidget.svelte";
import { ReviewsListWidgetConfigInterface } from "./ReviewsListWidgetConfig";

@injectable()
class ReviewsListWidget implements WidgetInterface {
    private readonly reviewsQuery: ReviewsQueryBase;
    private readonly reviewsService: ReviewsServiceBase;
    private readonly reviewsTranslationsQuery: ReviewsTranslationsQuery;
    private readonly reviewsTranslationsService: ReviewsTranslationsService;
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly modalManager: ModalManager;
    private readonly reviewsGalleryManager: ReviewsGalleryManager;
    private readonly localizationInfoProvider: LocalizationInfoProviderInterface;
    private readonly reviewsFilterService: ReviewsFilterService;
    private readonly reviewsFilterQuery: ReviewsFilterQuery;
    private readonly sortingManager: SortingManager;
    private readonly showReviewsFilterUIState: SimpleState<boolean>;
    private readonly showAddReviewButton: boolean;
    private readonly showAddReviewButtonInEmptyList: boolean;
    private readonly reviewsPerPage: number;
    private readonly authenticationInfoProvider: AuthenticationInfoProviderInterface;
    private readonly widgetName: string;

    constructor(
        private readonly config: ReviewsListWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("ReviewsListWidget is initialized", config);

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );
        this.showReviewsFilterUIState = this.config.showReviewsFilterUIState
            ? this.config.showReviewsFilterUIState
            : new SimpleState(false);

        this.showAddReviewButton = this.config.showAddReviewButton ?? true;
        this.showAddReviewButtonInEmptyList =
            this.config.showAddReviewButtonInEmptyList ?? true;
        this.widgetName = this.config.widgetName || "";

        if (this.config.reviewsService && this.config.reviewsQuery) {
            this.reviewsQuery = this.config.reviewsQuery;
            this.reviewsService = this.config.reviewsService;
        } else {
            const reviewsStore = new ReviewsStoreBase();
            this.reviewsQuery = new ReviewsQueryBase(reviewsStore);
            this.reviewsService = new ReviewsServiceBase(
                reviewsStore,
                container.resolve(VotesStore),
                container.resolve(reviewsAuthApiClientToken),
                container.resolve(reviewsApplicationLoggerToken)
            );
        }

        if (
            this.config.reviewsFilterService &&
            this.config.reviewsFilterQuery
        ) {
            this.reviewsFilterQuery = this.config.reviewsFilterQuery;
            this.reviewsFilterService = this.config.reviewsFilterService;
        } else {
            const reviewsFilterStore = new ReviewsFilterStore();
            this.reviewsFilterService = new ReviewsFilterService(
                reviewsFilterStore,
                container.resolve(reviewsAuthApiClientToken),
                this.logger
            );
            this.reviewsFilterQuery = new ReviewsFilterQuery(
                reviewsFilterStore
            );
        }

        this.reviewsTranslationsQuery = container.resolve(
            ReviewsTranslationsQuery
        );
        this.reviewsTranslationsService = container.resolve(
            ReviewsTranslationsService
        );

        this.modalManager = container.resolve(modalManagerToken);
        this.reviewsGalleryManager = container.resolve(ReviewsGalleryManager);

        this.localizationInfoProvider = container.resolve(
            localizationInfoProviderToken
        );

        this.sortingManager =
            this.config.sortingManager ||
            new SortingManager(
                this.reviewsStorefrontValuesService.selectReviewsSettings(),
                "reviews.ReviewsProductPageWidget"
            );
        this.reviewsPerPage = this.config.reviewsPerPage ?? 5;

        this.authenticationInfoProvider =
            container.resolve(
                AuthenticationModule
            ).exports.authenticationInfoProvider;
    }

    async init(): Promise<this> {
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "reviews.ReviewsProductPageWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.ReviewsProductPageWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "reviews.critical.ReviewsProductPageWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "reviews.critical.ReviewsProductPageWidget"
            )
        );

        // inject Customizations
        const customizationManager = container.resolve(CustomizationManager);
        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_LIST_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );
        contextMap.set(
            urlsBuilderContextToken,
            container.resolve(CommonModule).exports.urlsBuilder
        );

        new ReviewsListWidgetComponent({
            target: shadowRoot || element,
            props: {
                productIdManager: this.config.productIdManager,
                reviewsPerPage: this.reviewsPerPage,
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                reviewsQuery: this.reviewsQuery,
                reviewsService: this.reviewsService,
                reviewsTranslationsService: this.reviewsTranslationsService,
                reviewsTranslationsQuery: this.reviewsTranslationsQuery,
                localizationInfoProvider: this.localizationInfoProvider,
                modalManager: this.modalManager,
                reviewsGalleryManager: this.reviewsGalleryManager,
                reviewsFilterQuery: this.reviewsFilterQuery,
                reviewsFilterService: this.reviewsFilterService,
                sortingManager: this.sortingManager,
                showReviewsFilterUIState: this.showReviewsFilterUIState,
                showAddReviewButton: this.showAddReviewButton,
                showAddReviewButtonInEmptyList:
                    this.showAddReviewButtonInEmptyList,
                isNestedWidget: !this.config.isWidgetShadowRootEnabled,
                reviewsDefaultFilters: this.config.reviewsDefaultFilters,

                authenticationInfoProvider: this.authenticationInfoProvider,

                // Reviews Images Slider
                reviewsImagesSliderService:
                    this.config.reviewsImagesSliderService,

                widgetName: this.widgetName,

                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
            context: contextMap,
        });

        return this;
    }

    destroy() {
        // pass
    }
}

export default ReviewsListWidget;
